import type { Location } from 'react-router'
import type { ReactElement } from 'react'
import type { Store } from 'redux'
import { useDispatch, useStore } from 'react-redux'
import { useLocation, useNavigate, useSearchParams } from 'react-router'
import { useState } from 'react'

import { generateSearchUrl } from '../../../urlGenerators'
import { getPlain } from '../../../store/utils'
import { parseUrlQuery } from '../../../../utils/parseUrlQuery'
import { rangeFacetDelimiter } from '../../../utils/pageAndSort'
import { resetAllFilters, selectFacetedSearchFacetValue, setFacetedSearchFacetRange } from '../../../store/actions'
import { withQueryAndScope } from '../../../urlGeneratorsPlain'
import SearchFilterButton from './SearchFilterButton'
import SearchFilterOverlay from './SearchFilterOverlay'
import SearchFilters from './SearchFilters'
import SearchSortingForm from '../../themeComponents/epages.base/SearchSortingForm'
import ToolbarTop from '../ToolbarTop'
import buildURL from '../../../../utils/buildURL'

export const getFilters = (facets?: Core.Facets): { [key: string]: string[] } => {
  const filters = {}

  if (facets) {
    Object.values(facets).forEach((facet) => {
      switch (facet.type) {
        case 'selection':
          Object.values(facet.values).forEach((value) => {
            if (value.selected) {
              if (!Object.keys(filters).includes(facet.id)) {
                filters[facet.id] = [value.value]
              } else {
                filters[facet.id].push(value.value)
              }
            }
          })
          break
        case 'range':
          if (facet.values?.selected) {
            if (!Object.keys(filters).includes(facet.id)) {
              filters[facet.id] = [facet.values.selection.min + rangeFacetDelimiter + facet.values.selection.max]
            } else {
              filters[facet.id].push(facet.values.selection.min + rangeFacetDelimiter + facet.values.selection.max)
            }
          }
          break
      }
    })
  }
  return filters
}

export type Query = {
  q: string
  page: string
  sort: string
  // Any faceted search filter
  [key: string]: string | string[]
}

export type SearchOptions = {
  q?: string
  sort?: string
  page?: number
}

export function getSearchQuery(store: Store<State>, location: Location, options?: SearchOptions): string {
  const state = store.getState()
  const query = parseUrlQuery(location.search) as Query
  const defaultSorting = 'relevance'

  const currentOptions: Required<SearchOptions> = {
    q: query.q ?? '',
    page: 1,
    sort: query.sort ?? defaultSorting,
    ...options,
  }

  const facets = getPlain<Core.Facets | undefined>(state.getIn(['searchData', 'facets'], undefined))

  const searchParams = new URLSearchParams()
  searchParams.append('q', currentOptions.q)
  searchParams.append('sort', currentOptions.sort)
  searchParams.append('page', currentOptions.page.toString())
  const searchParamsObj = getFilters(facets)
  Object.entries(searchParamsObj).forEach(([key, value]) => {
    value.forEach((element) => searchParams.append(key, element))
  })

  return buildURL(withQueryAndScope(generateSearchUrl(), location) as string, searchParams)
}

type Props = {
  searchData: any
  isBusy: boolean
  onScrollIntoView: VoidFunction
} & TranslateProps

function FacetedSearchToolbarTop({ isBusy, onScrollIntoView, searchData, t }: Readonly<Props>): ReactElement {
  const [isOpen, setIsOpen] = useState(false)

  const store = useStore<State>()

  const [searchParams] = useSearchParams()
  const queryString = searchParams.get('q') ?? ''

  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleQueryFilterChange = () => {
    navigate(getSearchQuery(store, location, { q: '*' }), { replace: true })
  }

  const handleSelectionFilterChange = (facetId: string, value: Core.FacetSelectionValue) => {
    dispatch(selectFacetedSearchFacetValue(facetId, value.value, !value.selected))
    navigate(getSearchQuery(store, location), { replace: true })
    scrollToTop()
  }

  const handleRangeFilterChange = (facetId: string, range: { min: number; max: number }) => {
    dispatch(setFacetedSearchFacetRange(facetId, range))
    navigate(getSearchQuery(store, location), { replace: true })
    scrollToTop()
  }

  const handleResetAllCategoryFilters = () => {
    dispatch(resetAllFilters())
    navigate(getSearchQuery(store, location), { replace: true })
    scrollToTop()
  }

  return (
    <div className="faceted-search-toolbar-top-wrapper">
      <ToolbarTop className="faceted-search-toolbar-top" onScrollIntoView={onScrollIntoView}>
        <div className="search-configuration">
          <SearchSortingForm searchData={searchData} t={t} />
          <SearchFilterButton onClick={() => setIsOpen(true)}>
            {t('components.facetedSearch.filterButton.label')}
          </SearchFilterButton>
        </div>
        <SearchFilters
          facets={searchData.facets}
          queryString={queryString}
          onQueryFilterChange={handleQueryFilterChange}
          onSelectionFilterChange={handleSelectionFilterChange}
          onRangeFilterChange={handleRangeFilterChange}
        />
        <SearchFilterOverlay
          facets={searchData.facets}
          totalNumberOfProducts={searchData.totalNumberOfProducts || 0}
          isBusy={isBusy}
          isOpen={isOpen}
          onSelectionFacetChange={(facet, value) => {
            handleSelectionFilterChange(facet.id, value)
          }}
          onRangeFacetChange={(facet, range) => {
            handleRangeFilterChange(facet.id, range)
          }}
          onResetAllFilters={handleResetAllCategoryFilters}
          onRequestClose={() => setIsOpen(false)}
          t={t}
        />
      </ToolbarTop>
    </div>
  )
}

export default FacetedSearchToolbarTop
